import React, { useState, useEffect, useCallback } from "react";
import {
    Button,
    IconButton,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    makeStyles,
    Tabs,
    Tab,
    Box,
    Chip
} from "@material-ui/core";

import {
    DeleteOutline,
    Edit,
    Add
} from "@material-ui/icons";

import MainContainer from "../../components/MainContainer";
import MainHeader from "../../components/MainHeader";
import MainHeaderButtonsWrapper from "../../components/MainHeaderButtonsWrapper";
import Title from "../../components/Title";
import FunnelModal from "../../components/FunnelModal";
import FunnelCardModal from "../../components/FunnelCardModal";
import LossReasonModal from "../../components/LossReasonModal";
import OpportunityModal from "../../components/OpportunityModal";
import ConfirmationModal from "../../components/ConfirmationModal";
import toastError from "../../errors/toastError";
import { i18n } from "../../translate/i18n";
import { toast } from "react-toastify";
import { getFunnels, deleteFunnel } from "../../services/funnels";
import { getFunnelCards, deleteFunnelCard } from "../../services/funnelCards";
import { getLossReasons, deleteLossReason } from "../../services/lossReasons";
import { getFunnelOpportunities, deleteFunnelOpportunity } from "../../services/funnelOpportunities";

const useStyles = makeStyles(theme => ({
    mainPaper: {
        flex: 1,
        padding: theme.spacing(1),
        overflowY: "scroll",
        ...theme.scrollbarStyles,
    },
    colorBox: {
        width: 24,
        height: 24,
        borderRadius: 12,
        margin: 'auto',
        border: '1px solid #ccc'
    }
}));

function TabPanel(props) {
    const { children, value, index, ...other } = props;
    return (
        <div
            role="tabpanel"
            hidden={value !== index}
            {...other}
        >
            {value === index && <Box p={3}>{children}</Box>}
        </div>
    );
}

const KanbanManagement = () => {
    const classes = useStyles();
    const [tabValue, setTabValue] = useState(0);

    // Funis
    const [funnels, setFunnels] = useState([]);
    const [funnelModalOpen, setFunnelModalOpen] = useState(false);
    const [selectedFunnel, setSelectedFunnel] = useState(null);

    // Cards
    const [cards, setCards] = useState([]);
    const [cardModalOpen, setCardModalOpen] = useState(false);
    const [selectedCard, setSelectedCard] = useState(null);
    const [selectedFunnelForCards, setSelectedFunnelForCards] = useState(null);

    // Motivos
    const [lossReasons, setLossReasons] = useState([]);
    const [lossReasonModalOpen, setLossReasonModalOpen] = useState(false);
    const [selectedLossReason, setSelectedLossReason] = useState(null);

    // Oportunidades
    const [opportunities, setOpportunities] = useState([]);
    const [opportunityModalOpen, setOpportunityModalOpen] = useState(false);
    const [selectedOpportunity, setSelectedOpportunity] = useState(null);
    const [selectedFunnelForOpp, setSelectedFunnelForOpp] = useState(null);

    // Confirmação
    const [confirmModalOpen, setConfirmModalOpen] = useState(false);
    const [deletingItem, setDeletingItem] = useState(null);
    const [deleteType, setDeleteType] = useState("");

    const fetchFunnels = useCallback(async () => {
        try {
            const { data } = await getFunnels();
            setFunnels(data);
            if (data.length > 0 && !selectedFunnelForCards) {
                setSelectedFunnelForCards(data[0].id);
                setSelectedFunnelForOpp(data[0].id);
            }
        } catch (err) {
            toastError(err);
        }
    }, []);

    const fetchCards = useCallback(async () => {
        if (!selectedFunnelForCards) return;
        try {
            const { data } = await getFunnelCards(selectedFunnelForCards);
            setCards(data);
        } catch (err) {
            toastError(err);
        }
    }, [selectedFunnelForCards]);

    const fetchLossReasons = useCallback(async () => {
        try {
            const { data } = await getLossReasons();
            setLossReasons(data);
        } catch (err) {
            toastError(err);
        }
    }, []);

    const fetchOpportunities = useCallback(async () => {
        if (!selectedFunnelForOpp) return;
        try {
            const { data } = await getFunnelOpportunities(selectedFunnelForOpp);
            setOpportunities(data);
        } catch (err) {
            toastError(err);
        }
    }, [selectedFunnelForOpp]);

    useEffect(() => {
        fetchFunnels();
        fetchLossReasons();
    }, [fetchFunnels, fetchLossReasons]);

    useEffect(() => {
        if (tabValue === 1) fetchCards();
    }, [tabValue, fetchCards]);

    useEffect(() => {
        if (tabValue === 3) fetchOpportunities();
    }, [tabValue, fetchOpportunities]);

    const handleDelete = async () => {
        try {
            if (deleteType === "funnel") {
                await deleteFunnel(deletingItem.id);
                toast.success(i18n.t("kanban.funnels.toasts.deleted"));
                fetchFunnels();
            } else if (deleteType === "card") {
                await deleteFunnelCard(deletingItem.id);
                toast.success(i18n.t("kanban.cards.toasts.deleted"));
                fetchCards();
            } else if (deleteType === "lossReason") {
                await deleteLossReason(deletingItem.id);
                toast.success(i18n.t("kanban.lossReasons.toasts.deleted"));
                fetchLossReasons();
            } else if (deleteType === "opportunity") {
                await deleteFunnelOpportunity(deletingItem.id);
                toast.success(i18n.t("kanban.opportunities.toasts.deleted"));
                fetchOpportunities();
            }
        } catch (err) {
            toastError(err);
        }
        setConfirmModalOpen(false);
        setDeletingItem(null);
    };

    return (
        <MainContainer>
            <FunnelModal
                open={funnelModalOpen}
                onClose={() => setFunnelModalOpen(false)}
                funnelId={selectedFunnel?.id}
                reload={fetchFunnels}
            />
            <FunnelCardModal
                open={cardModalOpen}
                onClose={() => setCardModalOpen(false)}
                funnelId={selectedFunnelForCards}
                card={selectedCard}
                reload={fetchCards}
            />
            <LossReasonModal
                open={lossReasonModalOpen}
                onClose={() => setLossReasonModalOpen(false)}
                lossReason={selectedLossReason}
                reload={fetchLossReasons}
            />
            <OpportunityModal
                open={opportunityModalOpen}
                onClose={() => setOpportunityModalOpen(false)}
                funnelId={selectedFunnelForOpp}
                opportunityId={selectedOpportunity?.id}
                reload={fetchOpportunities}
            />
            <ConfirmationModal
                title={i18n.t("kanban.confirmationModal.deleteTitle")}
                open={confirmModalOpen}
                onClose={() => setConfirmModalOpen(false)}
                onConfirm={handleDelete}
            >
                {i18n.t("kanban.confirmationModal.deleteMessage")}
            </ConfirmationModal>

            <MainHeader>
                <Title>{i18n.t("kanban.management.title")}</Title>
            </MainHeader>

            <Paper className={classes.mainPaper} variant="outlined">
                <Tabs value={tabValue} onChange={(e, newValue) => setTabValue(newValue)}>
                    <Tab label={i18n.t("kanban.tabs.funnels")} />
                    <Tab label={i18n.t("kanban.tabs.cards")} />
                    <Tab label={i18n.t("kanban.tabs.lossReasons")} />
                    <Tab label={i18n.t("kanban.tabs.opportunities")} />
                </Tabs>

                <TabPanel value={tabValue} index={0}>
                    <MainHeaderButtonsWrapper>
                        <Button
                            variant="contained"
                            color="primary"
                            onClick={() => {
                                setSelectedFunnel(null);
                                setFunnelModalOpen(true);
                            }}
                            startIcon={<Add />}
                        >
                            {i18n.t("kanban.funnels.buttons.add")}
                        </Button>
                    </MainHeaderButtonsWrapper>
                    <TableContainer>
                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell>{i18n.t("kanban.funnels.table.name")}</TableCell>
                                    <TableCell>{i18n.t("kanban.funnels.table.description")}</TableCell>
                                    <TableCell align="center">{i18n.t("kanban.funnels.table.actions")}</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {funnels.map((funnel) => (
                                    <TableRow key={funnel.id}>
                                        <TableCell>{funnel.name}</TableCell>
                                        <TableCell>{funnel.description}</TableCell>
                                        <TableCell align="center">
                                            <IconButton size="small" onClick={() => {
                                                setSelectedFunnel(funnel);
                                                setFunnelModalOpen(true);
                                            }}>
                                                <Edit />
                                            </IconButton>
                                            <IconButton size="small" onClick={() => {
                                                setDeletingItem(funnel);
                                                setDeleteType("funnel");
                                                setConfirmModalOpen(true);
                                            }}>
                                                <DeleteOutline />
                                            </IconButton>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </TabPanel>

                <TabPanel value={tabValue} index={1}>
                    <MainHeaderButtonsWrapper>
                        <select
                            value={selectedFunnelForCards || ""}
                            onChange={(e) => setSelectedFunnelForCards(parseInt(e.target.value))}
                            style={{ marginRight: 10, padding: 8 }}
                        >
                            {funnels.map((funnel) => (
                                <option key={funnel.id} value={funnel.id}>{funnel.name}</option>
                            ))}
                        </select>
                        <Button
                            variant="contained"
                            color="primary"
                            onClick={() => {
                                setSelectedCard(null);
                                setCardModalOpen(true);
                            }}
                            startIcon={<Add />}
                        >
                            {i18n.t("kanban.cards.buttons.add")}
                        </Button>
                    </MainHeaderButtonsWrapper>
                    <TableContainer>
                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell>{i18n.t("kanban.cards.table.name")}</TableCell>
                                    <TableCell align="center">{i18n.t("kanban.cards.table.type")}</TableCell>
                                    <TableCell align="center">{i18n.t("kanban.cards.table.percentage")}</TableCell>
                                    <TableCell align="center">{i18n.t("kanban.cards.table.color")}</TableCell>
                                    <TableCell align="center">{i18n.t("kanban.cards.table.actions")}</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {cards.map((card) => (
                                    <TableRow key={card.id}>
                                        <TableCell>{card.name}</TableCell>
                                        <TableCell align="center">
                                            <Chip label={i18n.t(`kanban.cards.types.${card.type}`)} size="small" />
                                        </TableCell>
                                        <TableCell align="center">
                                            {card.type === "controle" ? `${card.percentage}%` : "-"}
                                        </TableCell>
                                        <TableCell align="center">
                                            <div className={classes.colorBox} style={{ backgroundColor: card.color }} />
                                        </TableCell>
                                        <TableCell align="center">
                                            <IconButton size="small" onClick={() => {
                                                setSelectedCard(card);
                                                setCardModalOpen(true);
                                            }}>
                                                <Edit />
                                            </IconButton>
                                            <IconButton size="small" onClick={() => {
                                                setDeletingItem(card);
                                                setDeleteType("card");
                                                setConfirmModalOpen(true);
                                            }}>
                                                <DeleteOutline />
                                            </IconButton>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </TabPanel>

                <TabPanel value={tabValue} index={2}>
                    <MainHeaderButtonsWrapper>
                        <Button
                            variant="contained"
                            color="primary"
                            onClick={() => {
                                setSelectedLossReason(null);
                                setLossReasonModalOpen(true);
                            }}
                            startIcon={<Add />}
                        >
                            {i18n.t("kanban.lossReasons.buttons.add")}
                        </Button>
                    </MainHeaderButtonsWrapper>
                    <TableContainer>
                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell>{i18n.t("kanban.lossReasons.table.name")}</TableCell>
                                    <TableCell>{i18n.t("kanban.lossReasons.table.description")}</TableCell>
                                    <TableCell align="center">{i18n.t("kanban.lossReasons.table.actions")}</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {lossReasons.map((reason) => (
                                    <TableRow key={reason.id}>
                                        <TableCell>{reason.name}</TableCell>
                                        <TableCell>{reason.description}</TableCell>
                                        <TableCell align="center">
                                            <IconButton size="small" onClick={() => {
                                                setSelectedLossReason(reason);
                                                setLossReasonModalOpen(true);
                                            }}>
                                                <Edit />
                                            </IconButton>
                                            <IconButton size="small" onClick={() => {
                                                setDeletingItem(reason);
                                                setDeleteType("lossReason");
                                                setConfirmModalOpen(true);
                                            }}>
                                                <DeleteOutline />
                                            </IconButton>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </TabPanel>

                <TabPanel value={tabValue} index={3}>
                    <MainHeaderButtonsWrapper>
                        <select
                            value={selectedFunnelForOpp || ""}
                            onChange={(e) => setSelectedFunnelForOpp(parseInt(e.target.value))}
                            style={{ marginRight: 10, padding: 8 }}
                        >
                            {funnels.map((funnel) => (
                                <option key={funnel.id} value={funnel.id}>{funnel.name}</option>
                            ))}
                        </select>
                        <Button
                            variant="contained"
                            color="primary"
                            onClick={() => {
                                setSelectedOpportunity(null);
                                setOpportunityModalOpen(true);
                            }}
                            startIcon={<Add />}
                        >
                            {i18n.t("kanban.opportunities.buttons.add")}
                        </Button>
                    </MainHeaderButtonsWrapper>
                    <TableContainer>
                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell>{i18n.t("kanban.opportunities.table.title")}</TableCell>
                                    <TableCell>{i18n.t("kanban.opportunities.table.card")}</TableCell>
                                    <TableCell>{i18n.t("kanban.opportunities.table.contact")}</TableCell>
                                    <TableCell align="right">{i18n.t("kanban.opportunities.table.value")}</TableCell>
                                    <TableCell align="center">{i18n.t("kanban.opportunities.table.actions")}</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {opportunities.map((opp) => (
                                    <TableRow key={opp.id}>
                                        <TableCell>{opp.title}</TableCell>
                                        <TableCell>{opp.card?.name}</TableCell>
                                        <TableCell>{opp.contact?.name || "-"}</TableCell>
                                        <TableCell align="right">
                                            {opp.value ? `R$ ${parseFloat(opp.value).toFixed(2)}` : "-"}
                                        </TableCell>
                                        <TableCell align="center">
                                            <IconButton size="small" onClick={() => {
                                                setSelectedOpportunity(opp);
                                                setOpportunityModalOpen(true);
                                            }}>
                                                <Edit />
                                            </IconButton>
                                            <IconButton size="small" onClick={() => {
                                                setDeletingItem(opp);
                                                setDeleteType("opportunity");
                                                setConfirmModalOpen(true);
                                            }}>
                                                <DeleteOutline />
                                            </IconButton>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </TabPanel>
            </Paper>
        </MainContainer>
    );
};

export default KanbanManagement;
